import * as React from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, Crosshair, Trophy } from 'lucide-react'
import { sb } from '@/lib/supabase'
import { useAuth } from '@/auth/auth-provider'
import { aimAt, fire, HEIGHT, newGame, start, step, TARGET, WIDTH, type GameState, type Status } from '@/lib/missile-game'
import { standings } from '@/lib/lobby'
import type { LobbyMember } from '@/lib/types'
import { draw } from '@/pages/missile-defence'
import { LobbyRoom, useLobby, type LobbyState } from '@/components/team-lobby'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ErrorNote, Loading } from '@/components/page'

/**
 * Team Missile Defence (migrations/0061_lobbies.sql).
 *
 * The solo game (missile-defence.tsx) on every player's own screen, started for
 * all of them at once by the host. Each player has their own sky: the missiles,
 * spawns and hits come from lib/missile-game.ts running in this browser exactly
 * as in the solo game, and nothing about them is ever written or read across
 * devices. What IS shared is each player's count and whether they are out —
 * reported through `report_lobby_score` and shown to everyone in the
 * scoreboard beside the playfield.
 *
 * The lobby before the start is the common LobbyRoom; this page owns only what
 * happens after it.
 */
export function MissileDefenceTeamPage() {
  const { lobbyId } = useParams()
  const { session } = useAuth()
  const userId = session?.user?.id
  const { data, loading, error, refetch, signal } = useLobby(lobbyId)

  const changed = React.useCallback(() => {
    void refetch()
    signal()
  }, [refetch, signal])

  const lobby = data?.lobby ?? null
  const me = data?.members.find((m) => m.user_id === userId) ?? null

  const canvasRef = React.useRef<HTMLCanvasElement | null>(null)
  const game = React.useRef<GameState>(newGame())
  const [status, setStatus] = React.useState<Status>('ready')
  const [destroyed, setDestroyed] = React.useState(0)

  // Whether this screen's sky is running. Only while the lobby is started: if
  // the host ends a game somebody walked away from, the rest stop where they are
  // rather than playing on into a game that no longer takes their score.
  const running = lobby?.status === 'started' && Boolean(me) && me?.outcome == null
  const runningRef = React.useRef(running)
  runningRef.current = running

  // The host's Start reaches this page as the lobby turning 'started'; that is
  // the kickoff. A player who reloads mid-game resumes at the count they had
  // reported — their sky cannot be restored, but the ramp is by count, so the
  // difficulty picks up where it was, and the score never has to go down
  // (which 0061 refuses anyway).
  const meIn = Boolean(me)
  const myOutcome = me?.outcome ?? null
  const myReported = me?.score ?? 0
  React.useEffect(() => {
    if (lobby?.status !== 'started' || !meIn || myOutcome) return
    if (game.current.status !== 'ready') return
    game.current = { ...start(game.current), destroyed: myReported }
    // Kick off once; later score changes must not restart the round.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lobby?.status, meIn, myOutcome])

  React.useEffect(() => {
    let frame = 0
    let last = performance.now()
    const tick = (now: number) => {
      if (runningRef.current) game.current = step(game.current, (now - last) / 1000, Math.random)
      last = now
      const g = game.current
      setStatus((s) => (s === g.status ? s : g.status))
      setDestroyed((d) => (d === g.destroyed ? d : g.destroyed))
      if (canvasRef.current) draw(canvasRef.current, g)
      frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [])

  // The spacebar fires, as in the solo game — but never starts anything. The
  // host's Start is the only start, and there is no second round to restart.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.code !== 'Space') return
      const t = e.target as HTMLElement | null
      if (t && (t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName))) return
      if (game.current.status !== 'playing') return
      e.preventDefault()
      if (e.repeat || !runningRef.current) return
      game.current = fire(game.current)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // ---- Reporting --------------------------------------------------------------
  //
  // One request in flight at a time, always carrying the LATEST count. Firing a
  // request per hit would let them land out of order, and 0061 refuses a count
  // that goes down — so a late "11" after "12" would be an error, and a late
  // count after the outcome would be refused outright.
  const sending = React.useRef(false)
  const reported = React.useRef<{ score: number; outcome: string | null }>({ score: -1, outcome: null })
  const flush = React.useCallback(async () => {
    if (sending.current || !lobbyId || !runningRef.current) return
    const g = game.current
    if (g.status === 'ready') return
    const outcome = g.status === 'won' || g.status === 'lost' ? g.status : null
    if (g.destroyed === reported.current.score && outcome === reported.current.outcome) return
    sending.current = true
    const { error: err } = await sb().rpc('report_lobby_score', {
      p_lobby_id: lobbyId,
      p_score: g.destroyed,
      p_outcome: outcome,
    })
    sending.current = false
    // Recorded on success and on a refusal from 0061 (42501/22023): those are
    // final (the game ended, you are already out), and retrying them would be a
    // hot loop. Anything else — a network error, a 503 that outlived the retry —
    // is transient, and dropping it could lose the final outcome that finishes
    // the game, so try again shortly.
    if (err && err.code !== '42501' && err.code !== '22023') {
      setTimeout(() => void flush(), 2000)
      return
    }
    reported.current = { score: g.destroyed, outcome }
    if (!err) changed()
    void flush()
  }, [lobbyId, changed])

  React.useEffect(() => {
    void flush()
  }, [destroyed, status, flush])

  function onPointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
    const rect = e.currentTarget.getBoundingClientRect()
    const x = ((e.clientX - rect.left) / rect.width) * WIDTH
    const y = ((e.clientY - rect.top) / rect.height) * HEIGHT
    game.current = aimAt(game.current, x, y)
  }

  async function endGame() {
    if (!lobbyId) return
    const { error: err } = await sb().rpc('end_lobby', { p_lobby_id: lobbyId })
    if (!err) changed()
  }

  const header = (
    <div className="mb-4 flex flex-wrap items-center gap-3">
      <Button variant="ghost" size="icon-sm" asChild aria-label="Back to Games">
        <Link to="/games">
          <ArrowLeft />
        </Link>
      </Button>
      <div className="min-w-0">
        <h1 className="text-xl font-semibold leading-tight">Team Missile Defence</h1>
        <p className="text-xs text-muted-foreground">
          Your own sky, everyone's score. Aim with the mouse. Fire with the spacebar.
        </p>
      </div>
      {me && lobby?.status !== 'open' && (
        <div className="ml-auto">
          <Badge variant={status === 'won' ? 'success' : 'secondary'} className="tabular-nums">
            <Crosshair className="mr-1 h-3.5 w-3.5" />
            {destroyed} / {TARGET}
          </Badge>
        </div>
      )}
    </div>
  )

  if (loading && !data) return <Loading label="Finding the game…" />

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-6 sm:px-6">
      {header}
      {error && <ErrorNote error={error} />}

      {!lobby && !error && (
        <p className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          That game isn't there any more.
        </p>
      )}

      {lobby?.status === 'open' && data && (
        <LobbyRoom state={data} title="Team Missile Defence" onChanged={changed}>
          <p className="text-sm text-muted-foreground">
            Everyone plays their own round of Missile Defence on their own screen. Shoot down {TARGET}{' '}
            to win; let one reach the ground and you're out. You'll see how everyone else is doing as
            you go.
          </p>
        </LobbyRoom>
      )}

      {lobby?.status === 'cancelled' && (
        <p className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          The host called this game off.
        </p>
      )}

      {data && lobby && (lobby.status === 'started' || lobby.status === 'finished') && (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_16rem]">
          {me ? (
            <div className="relative overflow-hidden rounded-lg border bg-card">
              <canvas
                ref={canvasRef}
                width={WIDTH}
                height={HEIGHT}
                onPointerMove={onPointerMove}
                className="block h-auto w-full cursor-crosshair touch-none"
                aria-label="Missile Defence playfield"
              />
              {(status !== 'playing' || lobby.status === 'finished') && (
                <div className="absolute inset-0 flex items-center justify-center bg-background/70 p-4">
                  <div className="max-w-sm space-y-3 text-center">
                    {status === 'won' && (
                      <>
                        <Trophy className="mx-auto h-8 w-8 text-primary" />
                        <p className="text-lg font-semibold">All {TARGET} down.</p>
                      </>
                    )}
                    {status === 'lost' && (
                      <>
                        <p className="text-lg font-semibold">One got through.</p>
                        <p className="text-sm text-muted-foreground">
                          {destroyed} of {TARGET} shot down.
                        </p>
                      </>
                    )}
                    {/* 'ready' with an outcome on record is a reload after this
                        player was already done: the sky is gone, the result is not. */}
                    {status === 'ready' && me.outcome && (
                      <p className="text-lg font-semibold">
                        You finished with {me.score} of {TARGET}.
                      </p>
                    )}
                    {status === 'ready' && !me.outcome && lobby.status === 'started' && (
                      <p className="text-lg font-semibold">Here they come…</p>
                    )}
                    {lobby.status === 'finished' ? (
                      <>
                        <p className="text-sm text-muted-foreground">
                          Game over — {winnerLine(data)}
                        </p>
                        <Button asChild>
                          <Link to="/games">Back to Games</Link>
                        </Button>
                      </>
                    ) : (
                      (status !== 'ready' || me.outcome) && (
                        <p className="text-sm text-muted-foreground">Waiting for the others to finish.</p>
                      )
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            // Not in it. The roster locked at the host's Start, so all there is
            // to offer is the scoreboard.
            <p className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
              {lobby.status === 'started'
                ? "This game started without you — nobody can join once the missiles are falling. Here's how it's going."
                : 'This game is over.'}
            </p>
          )}

          <Scoreboard state={data} userId={userId} />
          {lobby.status === 'started' && lobby.host_id === userId && (
            <div className="lg:col-start-2">
              <Button variant="outline" size="sm" className="w-full" onClick={() => void endGame()}>
                End the game for everyone
              </Button>
              <p className="mt-1 text-xs text-muted-foreground">
                For when somebody has walked away. Scores stay as they are.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function winnerLine({ members, names }: LobbyState) {
  const top = standings(members)[0]
  if (!top) return 'nobody played.'
  return `${names[top.user_id] ?? 'Someone'} came out on top with ${top.score}.`
}

function outcomeBadge(m: LobbyMember) {
  if (m.outcome === 'won') return <Badge variant="success">All {TARGET} down</Badge>
  if (m.outcome === 'lost') return <Badge variant="destructive">Out</Badge>
  return <Badge variant="muted">Playing</Badge>
}

/** The shared state, and the only shared state: each player's count and whether they are out. */
function Scoreboard({ state, userId }: { state: LobbyState; userId: string | undefined }) {
  return (
    <Card className="h-fit">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Scoreboard</CardTitle>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {standings(state.members).map((m) => (
          <div
            key={m.id}
            className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-sm ${m.user_id === userId ? 'bg-muted' : ''}`}
          >
            <span className="min-w-0 flex-1 truncate">{state.names[m.user_id] ?? 'Someone'}</span>
            <span className="tabular-nums text-muted-foreground">
              {m.score}/{TARGET}
            </span>
            {outcomeBadge(m)}
          </div>
        ))}
      </CardContent>
    </Card>
  )
}
